import React, { Component } from 'react';
import Axios from 'axios';
import { Capacitor } from '@capacitor/core';
import i18n from '../../../../i18next';
import { Col } from 'reactstrap';

import { SERVER_URL, FRONT_END_URL } from '../../../../utils/Constants';
import { refreshStatusBarAfterInAppBrowser } from '../../../../utils/initStatusBar';
import { getQueryInfoFromUrl, getAllUrlParams } from '../../../../utils/CommonUtils';

class BamboraForm extends Component {
    constructor(props) {
        super(props);

        this.bamboraCapacitorHostedRedirect = this.bamboraCapacitorHostedRedirect.bind(this);
    }

    componentDidMount = () => {
        this.props.validateCardInput(true);
        this.completeOrder();
    };

    componentDidUpdate = (prevProps) => {
        if (prevProps.payment.isCardValid !== this.props.payment.isCardValid) {
            this.props.validateCardInput(true);
        }

        // need to save bambora data first and after that to redirect to bambora url
        if ((prevProps.paymentProvider.bamboraData !== this.props.paymentProvider.bamboraData)
            && this.props.paymentProvider.bamboraData) {
            setTimeout(() => this.bamboraRedirect(this.props.paymentProvider.bamboraData.hostedPaymentPageUrl), 2000);
        }
    }

    componentWillUnmount = () => {
        this.props.validateCardInput(false);
    };

    doAuthorize = async (event) => {
        event.preventDefault();
        this._orderCompleted = false; // Reset guard for new payment attempt
        this.props.processingPayment();

        const { customer, selectedRestaurant, shoppingCart } = this.props;
        const shoppingCartTotalWithGiftCard = this.props.giftCards.giftCards ? this.props.giftCards.giftCards.total : null;
        const redirectUrls = this.getMenuuRedirectUrl();

        let paymentIntentRequest = {
            customerId: customer.id,
            restaurantId: selectedRestaurant.restaurantId,
            amount: shoppingCartTotalWithGiftCard ? shoppingCartTotalWithGiftCard.toFixed(2) : shoppingCart.total,
            currency: selectedRestaurant.currency,
            bamboraNaAuthorizeRequest: {
                redirectUrl: encodeURIComponent(redirectUrls.successUrl),
                failRedirectUrl: encodeURIComponent(redirectUrls.failUrl)
            }
        }

        const header = { headers: { 'Authorization': `TOKEN${customer.token}` } };

        await Axios.post(`${SERVER_URL}/payment/authorize`, paymentIntentRequest, header)
            .then(response => {
                this.props.saveBamboraData(response.data.bamboraNaResponse);
            })
            .catch(error => {
                this.props.errorProcessingPayment(error, 'Authorize Failure', this.props.paymentProvider.providerType.id);
                this.props.sendEmailReport(selectedRestaurant.restaurantId, error, 'Bambora authorize error');
            });
    }

    bamboraCapacitorHostedRedirect = async (redirectUrl) => {
        if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.InAppBrowser && redirectUrl) {
            const InAppBrowser = window.Capacitor.Plugins.InAppBrowser;
            await InAppBrowser.addListener('browserPageNavigationCompleted', (event) => this.completeOrder(event, InAppBrowser));

            await InAppBrowser.addListener('browserClosed', () => {
                void refreshStatusBarAfterInAppBrowser();
                this.completeOrder(null, null);
            });
    
            const options = {
                showToolbar: true,
                showURL: false,
                clearCache: false,
                clearSessionCache: false,
                mediaPlaybackRequiresUserAction: false,
                closeButtonText: "Close",
                toolbarPosition: 0,
                showNavigationButtons: false,
                leftToRight: false,
                customWebViewUserAgent: null,
                android: {
                    allowZoom: false,
                    hardwareBack: true,
                    pauseMedia: true
                },
                iOS: {
                    allowOverScroll: true,
                    enableViewportScale: false,
                    allowInLineMediaPlayback: false,
                    surpressIncrementalRendering: false,
                    viewStyle: 2,
                    animationEffect: 2,
                    allowsBackForwardNavigationGestures: true
                }
            };
    
            await InAppBrowser.openInWebView({
                url: redirectUrl,
                options: options
            });
        }
    }

    bamboraRedirect = (redirectUrl) => {
        if (Capacitor.isNativePlatform()) {
            this.bamboraCapacitorHostedRedirect(redirectUrl);
        } else if (!this.props.pwaAppRunning) {
            window.location.href = redirectUrl;
        } else {
            let browserRef = window.cordova.InAppBrowser.open(redirectUrl, '_blank', 'location=no,toolbar=no'); // iOS only works with target = _blank
            browserRef.addEventListener('loadstart', (event) => this.completeOrder(event, browserRef));
        }
    }

    getMenuuRedirectUrl = () => {
        const { pwaAppRunning } = this.props;
        const { navigationType, brandId } = this.props.storeLocalStorage;
        const isIntegration = getQueryInfoFromUrl().brandId;
        let redirectUrls = {
            successUrl: '',
            failUrl: ''
        };

        if (isIntegration) {
            redirectUrls.successUrl = `${FRONT_END_URL}/?brandId=${brandId}&navigationType=${navigationType}&status=SUCCESS#/checkout/`;
            redirectUrls.failUrl = `${FRONT_END_URL}/?brandId=${brandId}&navigationType=${navigationType}&status=FAIL#/checkout/`;
        } else if (pwaAppRunning || Capacitor.isNativePlatform()) {
            redirectUrls.successUrl = `${FRONT_END_URL}/payment-redirect.html?success=true`;
            redirectUrls.failUrl = `${FRONT_END_URL}/payment-redirect.html?success=false`;
        } else {
            redirectUrls.successUrl = `${FRONT_END_URL}/?status=SUCCESS#/checkout/`;
            redirectUrls.failUrl = `${FRONT_END_URL}/?status=FAIL#/checkout/`;
        }

        return redirectUrls;
    }

    completeOrder = async (event, browserRef) => {
        if(this._orderCompleted){
            return;
        }
        const expectedRedirectBase = '/payment-redirect.html';
        if (new URLSearchParams(window.location.search).get('status')) {    // desktop and mobile view flow
            this._orderCompleted = true;
            const isSuccess = new URLSearchParams(window.location.search).get('status');

            if (isSuccess === 'SUCCESS') {
                const providerData = {
                    transactionId: new URLSearchParams(window.location.hash).get('trnId'),
                    amount: new URLSearchParams(window.location.hash).get('trnAmount')
                }

                this.props.placeOrder(false, providerData);
                this.props.clearBamboraData();
            } else {
                this.props.errorProcessingPayment({}, 'Something went wrong.', this.props.paymentProvider.providerType.id);
                this.props.clearBamboraData();
            }
        } else if (event && event.url && browserRef) { // apps flow

            const urlObj = new URL(event.url);
            if (urlObj.pathname.endsWith(expectedRedirectBase) && urlObj.searchParams.get('success') !== null) {
                this._orderCompleted = true;
                // Wait 2 seconds to show the payment-redirect.html page before closing
                await new Promise(resolve => setTimeout(resolve, 2000));

                if (Capacitor.isNativePlatform()) { // Capacitor app
                    await browserRef.removeAllListeners();
                } else { // Cordova app
                    browserRef.removeEventListener('loadstart', this.completeOrder);
                }

                browserRef.close();
                if (Capacitor.isNativePlatform()) {
                    void refreshStatusBarAfterInAppBrowser();
                }

                let allUrlParams = getAllUrlParams(event.url);
                const isSuccess = allUrlParams.success;

                // Bambora puts trnId and trnAmount in the hash portion of the URL
                // Parse hash params: get everything after # and parse as URL params
                let hashParams = {};
                const hashIndex = event.url.indexOf('#');
                if (hashIndex !== -1) {
                    const hashString = event.url.substring(hashIndex + 1);
                    const paramsStart = hashString.indexOf('trnId');
                    if (paramsStart !== -1) {
                        const paramsString = hashString.substring(paramsStart);
                        new URLSearchParams(paramsString).forEach((value, key) => {
                            hashParams[key] = value;
                        });
                    }
                }

                if (isSuccess === 'true') {
                    const providerData = {
                        transactionId: allUrlParams.trnId || hashParams.trnId,
                        amount: allUrlParams.trnAmount || hashParams.trnAmount
                    }

                    this.props.placeOrder(false, providerData);
                    this.props.clearBamboraData();
                } else {
                    this.props.errorProcessingPayment({}, 'Something went wrong.', this.props.paymentProvider.providerType.id);
                    this.props.clearBamboraData();
                }
            } else {
                return;
            }
        } else if (event === null && browserRef === null) { // browserClosed event (back button)
            this._orderCompleted = true;
            this.props.errorProcessingPayment({}, i18n.t('screens:shoppingCartActions.cancelledByUser'), this.props.paymentProvider.providerType.id);
            this.props.clearBamboraData();
            return;
        } else if (this.props.paymentProvider.bamboraData) {   // handle error when click back button or cancel transaction
            this._orderCompleted = true;
            this.props.errorProcessingPayment({}, i18n.t(`screens:shoppingCartActions.cancelledByUser`), this.props.paymentProvider.providerType.id);
            this.props.clearBamboraData();
        }
    }

    render = () => {
        const { primaryFontColor } = this.props.customerThemes.selectedTheme;
        const { cardsInfoMessage } = this.props.styles;
        const isMobileView = window.innerWidth <= 865;

        return (
            <Col>
            <form
                id='bamboraForm'
                onSubmit={this.doAuthorize}
                hidden={true}
            />
            <p
                hidden={this.props.payment.selectedPaymentType.id !== 1}
                style={!this.props.pwaAppRunning && isMobileView ? {...cardsInfoMessage, color: primaryFontColor, position: 'none', left: 'unset'} :
                {...cardsInfoMessage, color: primaryFontColor}}>*{i18n.t('common:alerts.paymentProvidersWarning')}</p>
            </Col>
        )
    }
}
export default BamboraForm;